// ADAPTER (browser, under RLS) — CapabilityStore over the `my_capabilities()`
// RPC added by migration 0058.
//
// It lives under `/adapters/client` rather than `/adapters` for the reason
// recorded in eslint.config.js: `/adapters` is the SERVER-only entry (service
// role, Claude, Web Push) and importing it from a SPA drags server-only modules
// into the browser's module graph — a Cloudflare Pages resolve failure that
// local builds and CI both tree-shook away and missed. This runs in the browser
// under the caller's own session, so it belongs on the client side of that
// split.
//
// This is the ONLY file in the capability layer permitted to name the RPC.

import type { Capability, CapabilityStore } from '../../capability.js';

/**
 * The slice of supabase-js this adapter needs. STRUCTURAL, like
 * `TotpAuthClient` in platform-ui and for the same two reasons: it keeps
 * `@lemontaigu/platform` dependency-free (check E4 — importing
 * `@supabase/supabase-js` here would break it), and it lets a test pass a plain
 * object instead of a mocked client.
 */
export interface CapabilityRpcClient {
  // PromiseLike, not Promise: supabase-js returns a PostgrestFilterBuilder that
  // is thenable but is not a Promise (no `catch`/`finally`), so requiring
  // Promise here rejects the real client while accepting every fake.
  rpc(fn: 'my_capabilities'): PromiseLike<{ data: unknown; error: { message: string } | null }>;
}

export function createSupabaseCapabilityStore(client: CapabilityRpcClient): CapabilityStore {
  return {
    async mine(): Promise<readonly Capability[]> {
      const { data, error } = await client.rpc('my_capabilities');
      if (error) throw new Error(`my_capabilities: ${error.message}`);
      // The RPC returns text[]; PostgREST gives us a JSON array. A null or a
      // non-array is treated as "no capabilities" rather than as a crash —
      // failing CLOSED is the only safe direction for an authorization read.
      return Array.isArray(data) ? (data.filter((x) => typeof x === 'string') as Capability[]) : [];
    },
  };
}
